import { useEffect } from "react";

export interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: string;
  jsonLd?: object[];
  ogImage?: string;
}

const SITE_URL = "https://pockettoon.github.io";
const SITE_NAME = "抖漫";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function useSeo({
  title,
  description,
  keywords,
  path,
  type = "website",
  jsonLd = [],
  ogImage = `${SITE_URL}/og-cover.jpg`,
}: SeoOptions) {
  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);

    const url = `${SITE_URL}${path}`;
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    const old = document.head.querySelectorAll('script[data-jsonld="true"]');
    old.forEach((n) => n.remove());

    const fullJsonLd: object[] = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        url,
        description,
        isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
        inLanguage: "zh-CN",
      },
      ...jsonLd,
    ];

    fullJsonLd.forEach((data) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-jsonld", "true");
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      old.forEach((n) => n.remove());
      document.head.querySelectorAll('script[data-jsonld="true"]').forEach((n) => n.remove());
    };
  }, [title, description, keywords, path, type, ogImage]);
}

export { SITE_URL, SITE_NAME };